import { useId, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { focalStyle } from '@/features/storefront/focal';
import { useShopContent } from '@/features/storefront/content/useContent';
import {
  BMI_BANDS,
  BMI_SCALE,
  bmiAge,
  bmiScalePercent,
  calculateBmi,
  convertHeight,
  convertWeight,
  defaultBmiUnits,
  type BmiCategory,
  type BmiHeightUnit,
  type BmiResult,
  type BmiWeightUnit,
} from '@/features/storefront/sections/bmi';
import type { BmiCalculatorSection } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * A short height-and-weight form that works out a shopper's BMI.
 *
 * Client-side only: what a shopper types is never stored or sent, so the form
 * works the same in the customiser as on the shop. The form stands on a soft
 * card; height and weight each switch unit on the spot, carrying over what was
 * typed; an optional age keeps the adult categories from being read onto a
 * child. The result is one sentence in a polite live region, the WHO adult
 * category in its colour, a bar marking where the number stands, the
 * merchant's own line for that category and their button.
 */

export interface BmiCalculatorRenderers {
  /** A piece of the section's copy, typeable in the customiser. */
  text: (field: 'title' | 'subtitle' | 'note' | 'cta_label', value: string) => ReactNode;
  /** A link that is inert in the customiser and routed on the shop. */
  link: (href: string, className: string, style: CSSProperties, children: ReactNode) => ReactNode;
}

export interface BmiCalculatorViewProps extends BmiCalculatorRenderers {
  section: BmiCalculatorSection;
}

const CATEGORY_KEYS: Record<BmiCategory, string> = {
  under: 'section.bmi_underweight',
  healthy: 'section.bmi_healthy',
  over: 'section.bmi_overweight',
  obese: 'section.bmi_obese',
};

/** The bar's four bands, from cool to warm, laid over the page's own background. */
const BAND_COLORS: Record<BmiCategory, string> = {
  under: '#60a5fa',
  healthy: '#34d399',
  over: '#fbbf24',
  obese: '#f87171',
};

/** The merchant's own line under each result. */
export function bmiMessage(section: BmiCalculatorSection, category: BmiCategory): string {
  const message = {
    under: section.message_under,
    healthy: section.message_healthy,
    over: section.message_over,
    obese: section.message_obese,
  }[category];
  return message.trim();
}

/** The card the form stands on: near-white, a touch of the page in it. */
const CARD_FILL = 'color-mix(in srgb, var(--storefront-background) 15%, #ffffff)';

const FIELD_STYLE: CSSProperties = {
  borderColor: 'color-mix(in srgb, var(--storefront-muted) 35%, transparent)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-text)',
};

function NumberField({
  id,
  label,
  unit,
  value,
  onChange,
  hideLabel = false,
}: {
  id: string;
  label: string;
  unit: string;
  value: string;
  onChange: (next: string) => void;
  /** The label is still read out, but a row above already says it. */
  hideLabel?: boolean;
}) {
  return (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'mb-1.5 block text-sm font-semibold'}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step="any"
          min={0}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-12 w-full rounded-full border px-5 pe-14 text-sm outline-none transition-shadow focus:ring-2 focus:ring-[var(--storefront-primary)]/40"
          style={FIELD_STYLE}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 end-5 flex items-center text-sm"
          style={{ color: 'var(--storefront-muted)' }}
        >
          {unit}
        </span>
      </div>
    </div>
  );
}

/** Two units side by side; the one in use is filled with the theme's primary. */
function UnitSwitch<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex rounded-full border p-0.5 text-xs font-semibold"
      style={{ borderColor: FIELD_STYLE.borderColor }}
      data-bmi-unit-switch=""
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(option.value)}
            className="rounded-full px-3 py-1 uppercase tracking-wide transition-colors"
            style={
              on
                ? { backgroundColor: 'var(--storefront-primary)', color: 'var(--storefront-background)' }
                : { color: 'var(--storefront-muted)' }
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A measure's heading row: what it is on the left, its unit switch on the right. */
function MeasureHead({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center justify-between gap-3">
      <span className="text-sm font-semibold">{label}</span>
      {children}
    </div>
  );
}

function BmiScale({ result, label }: { result: BmiResult; label: string }) {
  const span = BMI_SCALE.max - BMI_SCALE.min;
  // Where one category gives way to the next, written under the bar.
  const edges = BMI_BANDS.slice(1).map((band) => band.from);
  return (
    <div className="mt-5" role="img" aria-label={`${label}: ${result.value}`} data-bmi-scale>
      <div className="relative">
        <div className="flex h-3 overflow-hidden rounded-full">
          {BMI_BANDS.map((band) => (
            <span
              key={band.category}
              style={{
                width: `${((band.to - band.from) / span) * 100}%`,
                backgroundColor: BAND_COLORS[band.category],
                opacity: band.category === result.category ? 1 : 0.45,
              }}
            />
          ))}
        </div>
        <span
          data-bmi-marker
          className="absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] shadow"
          style={{
            left: `${bmiScalePercent(result.value)}%`,
            backgroundColor: '#ffffff',
            borderColor: BAND_COLORS[result.category],
          }}
        />
      </div>
      <div aria-hidden className="relative mt-1.5 h-4 text-[11px]" style={{ color: 'var(--storefront-muted)' }}>
        {edges.map((edge) => (
          <span
            key={edge}
            className="absolute -translate-x-1/2"
            style={{ left: `${bmiScalePercent(edge)}%` }}
          >
            {edge}
          </span>
        ))}
      </div>
    </div>
  );
}

type BmiState = BmiResult | 'invalid' | 'invalid_age' | 'child' | null;

export function BmiCalculatorView({ section, text, link }: BmiCalculatorViewProps) {
  const copy = useShopContent();
  const id = useId();
  const opening = defaultBmiUnits(section.units);
  const [heightUnit, setHeightUnit] = useState<BmiHeightUnit>(opening.height);
  const [weightUnit, setWeightUnit] = useState<BmiWeightUnit>(opening.weight);
  const [height, setHeight] = useState('');
  const [inches, setInches] = useState('');
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [state, setState] = useState<BmiState>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const ageReading = bmiAge(age);
    if (ageReading === 'invalid') {
      setState('invalid_age');
      return;
    }
    const bmi = calculateBmi({ units: section.units, heightUnit, weightUnit, height, inches, weight });
    if (!bmi) setState('invalid');
    else setState(ageReading === 'child' ? 'child' : bmi);
  }

  function switchHeight(next: BmiHeightUnit) {
    if (next === heightUnit) return;
    const converted = convertHeight(next, height, inches);
    setHeight(converted.height);
    setInches(converted.inches);
    setHeightUnit(next);
  }

  function switchWeight(next: BmiWeightUnit) {
    if (next === weightUnit) return;
    setWeight(convertWeight(next, weight));
    setWeightUnit(next);
  }

  const result = state !== null && typeof state === 'object' ? state : null;
  const message = result ? bmiMessage(section, result.category) : '';
  const cta = section.cta_label.trim();
  const heightLabel = copy('section.bmi_height');
  const weightLabel = copy('section.bmi_weight');

  const form = (
    <div
      className="p-6 shadow-sm sm:p-10"
      style={{ backgroundColor: CARD_FILL, borderRadius: 'max(var(--storefront-radius), 1.25rem)' }}
      data-bmi-card=""
    >
      <h2 className="text-2xl tracking-tight sm:text-3xl" style={STOREFRONT_HEADING_STYLE}>
        {text('title', section.title)}
      </h2>
      <span
        aria-hidden
        className="mt-3 block h-1 w-14 rounded-full"
        style={{ backgroundColor: 'var(--storefront-primary)' }}
      />
      {section.subtitle ? (
        <p className="mt-3 text-base" style={{ color: 'var(--storefront-muted)' }}>
          {text('subtitle', section.subtitle)}
        </p>
      ) : null}
      <form
        className="mt-7 grid gap-5 sm:grid-cols-2"
        onSubmit={submit}
        noValidate
        data-bmi-units={section.units}
      >
        <div className="min-w-0">
          <MeasureHead label={heightLabel}>
            <UnitSwitch
              label={`${heightLabel}: ${copy('section.bmi_cm')} / ${copy('section.bmi_ft')}`}
              value={heightUnit}
              options={[
                { value: 'cm', label: copy('section.bmi_cm') },
                { value: 'ft', label: `${copy('section.bmi_ft')} ${copy('section.bmi_in')}` },
              ]}
              onChange={switchHeight}
            />
          </MeasureHead>
          {heightUnit === 'ft' ? (
            <fieldset className="min-w-0">
              <legend className="sr-only">{heightLabel}</legend>
              <div className="flex gap-3">
                <NumberField
                  id={`${id}-feet`}
                  label={`${heightLabel} (${copy('section.bmi_ft')})`}
                  unit={copy('section.bmi_ft')}
                  value={height}
                  onChange={setHeight}
                  hideLabel
                />
                <NumberField
                  id={`${id}-inches`}
                  label={`${heightLabel} (${copy('section.bmi_in')})`}
                  unit={copy('section.bmi_in')}
                  value={inches}
                  onChange={setInches}
                  hideLabel
                />
              </div>
            </fieldset>
          ) : (
            <NumberField
              id={`${id}-height`}
              label={`${heightLabel} (${copy('section.bmi_cm')})`}
              unit={copy('section.bmi_cm')}
              value={height}
              onChange={setHeight}
              hideLabel
            />
          )}
        </div>
        <div className="min-w-0">
          <MeasureHead label={weightLabel}>
            <UnitSwitch
              label={`${weightLabel}: ${copy('section.bmi_kg')} / ${copy('section.bmi_lb')}`}
              value={weightUnit}
              options={[
                { value: 'kg', label: copy('section.bmi_kg') },
                { value: 'lb', label: copy('section.bmi_lb') },
              ]}
              onChange={switchWeight}
            />
          </MeasureHead>
          <NumberField
            id={`${id}-weight`}
            label={`${weightLabel} (${copy(weightUnit === 'lb' ? 'section.bmi_lb' : 'section.bmi_kg')})`}
            unit={copy(weightUnit === 'lb' ? 'section.bmi_lb' : 'section.bmi_kg')}
            value={weight}
            onChange={setWeight}
            hideLabel
          />
        </div>
        <div className="min-w-0">
          <MeasureHead label={copy('section.bmi_age')}>
            <span className="text-xs" style={{ color: 'var(--storefront-muted)' }}>
              {copy('section.bmi_optional')}
            </span>
          </MeasureHead>
          <NumberField
            id={`${id}-age`}
            label={copy('section.bmi_age')}
            unit={copy('section.bmi_years')}
            value={age}
            onChange={setAge}
            hideLabel
          />
        </div>
        <div className="flex min-w-0 items-end">
          <button
            type="submit"
            className="h-12 w-full px-6 text-sm font-semibold"
            style={{ ...STOREFRONT_BUTTON_STYLE, borderRadius: '9999px' }}
          >
            {copy('section.bmi_calculate')}
          </button>
        </div>
      </form>
      <div aria-live="polite" className="mt-6" data-bmi-result>
        {state === 'invalid' || state === 'invalid_age' ? (
          <p className="text-sm" data-bmi-invalid style={{ color: 'var(--storefront-muted)' }}>
            {copy(state === 'invalid' ? 'section.bmi_invalid' : 'section.bmi_invalid_age')}
          </p>
        ) : null}
        {state === 'child' ? (
          <p className="text-sm" data-bmi-child style={{ color: 'var(--storefront-muted)' }}>
            {copy('section.bmi_adults_only')}
          </p>
        ) : null}
        {result ? (
          <div
            className="overflow-hidden border-s-4 p-5 sm:p-6"
            style={{
              borderRadius: 'max(var(--storefront-radius), 1rem)',
              borderColor: BAND_COLORS[result.category],
              backgroundColor: `color-mix(in srgb, ${BAND_COLORS[result.category]} 14%, var(--storefront-background))`,
            }}
            data-bmi-category={result.category}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-2xl sm:text-3xl" style={STOREFRONT_HEADING_STYLE}>
                {copy('section.bmi_result', { value: result.value.toFixed(1) })}
              </p>
              <p
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold"
                style={{ backgroundColor: BAND_COLORS[result.category], color: '#0b1220' }}
              >
                <span className="inline-block size-2 rounded-full bg-white/80" aria-hidden />
                <span>{copy(CATEGORY_KEYS[result.category])}</span>
              </p>
            </div>
            <BmiScale result={result} label={copy('section.bmi_scale')} />
            {message ? <p className="mt-4 text-sm">{message}</p> : null}
            {cta ? (
              <div className="mt-4">
                {link(
                  section.href.trim() || '/products',
                  'inline-flex h-11 items-center px-6 text-sm font-semibold',
                  STOREFRONT_BUTTON_STYLE,
                  text('cta_label', cta),
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {section.note.trim() ? (
        <p className="mt-4 text-xs" style={{ color: 'var(--storefront-muted)' }}>
          {text('note', section.note)}
        </p>
      ) : null}
    </div>
  );

  if (section.image_side === 'none' || !section.image_url) {
    return <div className="mx-auto max-w-3xl">{form}</div>;
  }
  return (
    <div data-bmi-photo={section.image_side} className="grid gap-10 lg:grid-cols-2 lg:items-stretch">
      {/* On a wide screen the photo takes the card's height, cropped to fill
          it - never a tall picture beside a short form. Its own shape on a phone. */}
      <div
        className={cn(
          'relative aspect-[4/5] overflow-hidden lg:aspect-auto',
          section.image_side === 'right' && 'lg:order-2',
        )}
        style={{ borderRadius: 'max(var(--storefront-radius), 1.25rem)' }}
        data-bmi-photo-frame=""
      >
        <StorefrontImage
          url={section.image_url}
          sizes="(min-width: 1024px) 50vw, 100vw"
          alt=""
          className="absolute inset-0 size-full object-cover"
          style={focalStyle(section.image_focal)}
        />
      </div>
      {form}
    </div>
  );
}
